import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, Skeleton } from '@dreamtree-org/twreact-ui';
import { ArrowRight } from 'lucide-react';
import { iconFor } from '../lib/typeIcons';
import { TenantPage } from '@dreamtree-org/landing-kit';
import '@dreamtree-org/landing-kit/styles.css';
import { EmptyState } from '@/components';
import { useAppApi } from '@/lib/api';
import { errorText } from '@/lib/format';
import { shopEntries } from '../lib/shop';
import { sortListings } from '../lib/filters';
import { useStorefront } from '../StorefrontContext';
import ListingCard from '../components/ListingCard';
import CatalogGate from '../components/CatalogGate';
import { RouterLink } from '../design/Region';
import { withoutPlaceholderBlocks } from '../lib/placeholders';


/**
 * WEB-01 landing: the store's own page (D24) — hero slider and the sections the
 * merchant arranged on the Appearance page, rendered by landing-kit's TenantPage.
 * "Featured" and "categories" are filled here from the live catalog.
 */
// Where this page load started: the server answered `/` with the SPA only when no custom home was live (or it failed).
const LOADED_AT = typeof window !== 'undefined' ? window.location.pathname : '/';

/** A live custom HTML home (ADR 0007) is the server's page: reach it with a full load, never a client route. */
export const reloadForCustomHome = (appearance, { loadedAt = LOADED_AT, previewing = false } = {}) =>
  Boolean(appearance && appearance.custom_home && loadedAt !== '/' && !previewing);

export default function Home() {
  const sf = useStorefront();
  const { fetchApi } = useAppApi();
  const a = sf.appearance;
  const custom = reloadForCustomHome(a, { previewing: sf.previewing });
  useEffect(() => {
    if (custom) window.location.assign('/');
  }, [custom]);
  if (custom) return <Skeleton height={420} />;

  if (!a) {
    return sf.appearanceQuery.error ? (
      <div className="mx-auto max-w-screen-xl px-4 py-8"><EmptyState title="This store's page could not be loaded">{sf.appearanceQuery.error}</EmptyState></div>
    ) : <Skeleton height={420} />;
  }

  const subscribe = async ({ email, consent }) => {
    try {
      await fetchApi('sfSubscribe', { email, consent }, false);
    } catch (ex) {
      throw new Error(errorText(ex));
    }
  };

  return (
    <TenantPage
      // A text section still holding landing-kit's sample copy is left out (MERCH-55).
      page={{ ...sf.doc.pages.home, sections: withoutPlaceholderBlocks(sf.doc.pages.home.sections) }}
      // Light/dark follows the storefront (the store's default mode is applied app-wide).
      theme={{ ...a.theme, mode: 'auto' }}
      storeName={sf.seller.name}
      resolveMedia={(ref) => sf.mediaUrl(ref)}
      linkComponent={RouterLink}
      onSubscribe={subscribe}
      slots={{ featured: (p) => <FeaturedListings limit={Number(p.limit) || 8} />, categories: <CategoryTiles /> }}
    />
  );
}

// One column of stacked cards would push stock below the fold; two fit a 360px phone.
const FEATURED_GRID = 'grid min-w-0 grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3 xl:grid-cols-4';

function FeaturedListings({ limit }) {
  const sf = useStorefront();
  const featured = sortListings(sf.listings, 'newest').slice(0, limit);
  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Link to={sf.shop.single ? `/shop/${sf.shop.single.slug}` : '/shop'} className="inline-flex min-h-11 items-center gap-1 text-sm font-medium text-primary-700 hover:underline">View all <ArrowRight className="h-4 w-4" /></Link>
      </div>
      <CatalogGate>
        {featured.length ? (
          // Two stacked cards per row on phones, so stock shows within the first screens (MERCH-57).
          <div className={FEATURED_GRID} data-testid="featured-grid">
            {featured.map((l) => <ListingCard key={l.id} l={l} layout="stack" />)}
          </div>
        ) : (
          <EmptyState title="Nothing listed yet">New stock is being inspected. Book a repair or sell us your device meanwhile.</EmptyState>
        )}
      </CatalogGate>
    </div>
  );
}

function CategoryTiles() {
  const sf = useStorefront();
  return (
    <div className="-mx-4 flex gap-3 overflow-x-auto px-4 pb-1 sm:mx-0 sm:grid sm:grid-cols-3 sm:px-0 lg:grid-cols-6">
      {shopEntries(sf.shop).map((e) => (
        <CategoryTile key={e.id} to={e.to} icon={iconFor(e.icon)} label={e.label} hint={e.id === 'all' ? `${sf.listings.length || ''} in stock`.trim() : e.hint} />
      ))}
    </div>
  );
}

function CategoryTile({ to, icon: Icon, label, hint }) {
  return (
    <Link to={to} className="group block w-32 shrink-0 sm:w-auto">
      <Card hover className="flex h-full flex-col items-center gap-2 p-3 text-center sm:p-4">
        <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary-50 text-primary-600 group-hover:bg-primary-100"><Icon className="h-6 w-6" aria-hidden="true" /></span>
        <span className="text-sm font-semibold">{label}</span>
        {hint ? <span className="text-xs opacity-60">{hint}</span> : null}
      </Card>
    </Link>
  );
}
